// ----- phase-small-findings-batch-1 (item 3) -----
//
// What each Letterhead mode means, written FROM the code rather than from the
// control's name. `requires` names the asset the mode needs; when it is absent
// the form says so rather than leaving a mode selected that cannot take effect.
export const LETTERHEAD_MODES: ReadonlyArray<{
  readonly value: string;
  readonly label: string;
  readonly help: string;
  readonly requires: 'logo' | 'letterhead' | null;
}> = [
  {
    value: 'none',
    label: 'None',
    help: 'Cover letters are plain text on an unbranded page — no logo, no letterhead artwork.',
    requires: null,
  },
  {
    value: 'logo_only',
    label: 'Logo only',
    help: 'The company logo is placed on the first page of the generated PDF; the rest of the page is unbranded.',
    requires: 'logo',
  },
  {
    value: 'full_letterhead',
    label: 'Full letterhead',
    help: 'The uploaded letterhead (PDF or Word) supplies the page artwork, and the letter text is laid out inside it.',
    requires: 'letterhead',
  },
];

/**
 * The "you have chosen a mode whose asset is missing" sentence, or null when
 * there is nothing to say. Exported for its unit test — the rule is one
 * function, not a condition spelled out inside JSX.
 */
export function letterheadModeGapMessage(
  mode: string,
  hasLogo: boolean,
  hasLetterhead: boolean,
): string | null {
  const spec = LETTERHEAD_MODES.find((m) => m.value === mode);
  if (!spec || spec.requires === null) return null;
  if (spec.requires === 'logo' && !hasLogo) {
    return '“Logo only” is selected, but no company logo is uploaded for this profile. Until one is, this mode has nothing to place and a generated cover letter will come out unbranded.';
  }
  if (spec.requires === 'letterhead' && !hasLetterhead) {
    return '“Full letterhead” is selected, but no letterhead file is uploaded for this profile. Until one is, this mode has no artwork to lay the letter into and a generated cover letter will come out unbranded.';
  }
  return null;
}

/** True when a template is a cover letter — matched on the same words a
 *  regulatory reader would use, since there is no structured type column.
 *  The description counts: the RFI-response templates are named for the
 *  request they answer ("FDA Information Request Response") and say "Response
 *  cover letter" only in their description, and they ARE cover letters. */
export function isCoverLetterTemplate(t: { Name: string; Description?: string | null }): boolean {
  return /cover\s*letter/i.test(`${t.Name} ${t.Description ?? ''}`);
}

/**
 * The cover-letter templates to offer, most-obviously-a-cover-letter first:
 * a template whose NAME says "cover letter" outranks one that only says so in
 * its description, so the chooser does not default to an RFI-response letter
 * when a plain cover letter exists. Stable within each group.
 */
export function coverLetterTemplatesFor<T extends { Name: string; Description?: string | null }>(
  templates: ReadonlyArray<T>,
): T[] {
  const matches = templates.filter(isCoverLetterTemplate);
  const byName = matches.filter((t) => /cover\s*letter/i.test(t.Name));
  const byDescription = matches.filter((t) => !/cover\s*letter/i.test(t.Name));
  return [...byName, ...byDescription];
}

/**
 * phase-company-profile-rows-7-9 (Row 9) — one sentence saying which company
 * profile letterhead a generated cover letter used, or why none was. The
 * server reports it on every generation; nothing about it is guessed here.
 */
export function describeLetterheadReport(report: {
  profileMode: string;
  source: 'region' | 'default' | 'none' | null;
  region: string | null;
  label: string | null;
  applied: boolean;
  reason: string | null;
}): string {
  if (report.applied) {
    const which =
      report.source === 'region'
        ? `the ${report.region ?? 'regional'} letterhead`
        : 'the default letterhead';
    return `Letterhead: ${which}${report.label ? ` (${report.label})` : ''}.`;
  }
  return `Letterhead not applied: ${report.reason ?? 'no reason was given.'}`;
}

/**
 * phase-company-profile-rows-7-9 (Row 9) — the regions a letterhead can be
 * filed under: the server's canonical region codes (src/lib/regions.ts), which
 * it stores every regional letterhead as. "Default" is deliberately absent —
 * the default is chosen with "Set as default", not filed as a region.
 */
export const LETTERHEAD_REGIONS = ['FDA', 'EMA', 'HC', 'MHRA', 'PMDA', 'TGA', 'Swissmedic'] as const;

/** Display name for a region code ("HC" reads as Health Canada). */
export function regionLabel(code: string): string {
  const lower = code.trim().toLowerCase();
  return lower === 'hc' || lower === 'healthcanada' || lower === 'health canada' ? 'Health Canada' : code;
}
